import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { Plus, Pencil, Trash2, Power, RotateCcw, Save, X } from "lucide-react";

const EMPTY = {
  nome: "", divisao: "", classe: "", palavra_chave: "", sexo: "ambos",
  peso_min: "", peso_max: "", altura_min_cm: "", altura_max_cm: "", idade_min: "", idade_max: "",
  experiencia: "", ativo: true, ordem: 0,
};
const PRESETS = [
  { label: "Júnior (≤23)", patch: { palavra_chave: "JÚNIOR", idade_max: 23 } },
  { label: "Teen (≤19)", patch: { palavra_chave: "TEEN", idade_max: 19 } },
  { label: "Masters 35+", patch: { palavra_chave: "35+", idade_min: 35 } },
  { label: "Masters 40+", patch: { palavra_chave: "40+", idade_min: 40 } },
  { label: "Masters 45+", patch: { palavra_chave: "45+", idade_min: 45 } },
  { label: "Masters 50+", patch: { palavra_chave: "50+", idade_min: 50 } },
  { label: "Novice", patch: { palavra_chave: "NOVICE", experiencia: "novice" } },
  { label: "True Novice", patch: { palavra_chave: "TRUE NOVICE", experiencia: "true_novice" } },
];
const num = (v) => (v === "" || v == null ? null : parseFloat(String(v).replace(",", ".")));
const SEXO = { M: "Masc.", F: "Fem.", ambos: "Ambos" };
const rng = (a, b, u) => (a == null && b == null ? "—" : a != null && b != null ? `${a}–${b} ${u}` : b != null ? `até ${b} ${u}` : `> ${a} ${u}`);

export const DivisionRulesEditor = () => {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(null); // null = fechado; {id?} = editando/criando
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/division-rules");
      setRules(data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };
  useEffect(() => { load(); }, []);

  const toForm = (r) => ({
    ...EMPTY, ...r,
    peso_min: r.peso_min ?? "", peso_max: r.peso_max ?? "", altura_min_cm: r.altura_min_cm ?? "", altura_max_cm: r.altura_max_cm ?? "",
    idade_min: r.idade_min ?? "", idade_max: r.idade_max ?? "", experiencia: r.experiencia || "",
  });
  const payload = (f) => ({
    nome: f.nome, divisao: f.divisao, classe: f.classe, palavra_chave: f.palavra_chave, sexo: f.sexo,
    peso_min: num(f.peso_min), peso_max: num(f.peso_max), altura_min_cm: num(f.altura_min_cm), altura_max_cm: num(f.altura_max_cm),
    idade_min: f.idade_min === "" ? null : parseInt(f.idade_min), idade_max: f.idade_max === "" ? null : parseInt(f.idade_max),
    experiencia: f.experiencia || null, ativo: !!f.ativo, ordem: f.ordem || 0,
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (!form.divisao && !form.palavra_chave) throw new Error("Informe a divisão (palavra-chave no nome da categoria) ou uma palavra-chave extra.");
      if (form.id) await api.put(`/division-rules/${form.id}`, payload(form));
      else await api.post("/division-rules", payload(form));
      setForm(null);
      await load();
    } catch (err) {
      setError(err?.response ? extractErrorMessage(err) : err.message);
    } finally { setBusy(false); }
  };
  const toggle = async (r) => {
    try { await api.put(`/division-rules/${r.id}`, { ...payload(toForm(r)), ativo: !r.ativo }); await load(); }
    catch (e) { setError(extractErrorMessage(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Excluir a regra "${r.nome}"? Para apenas pausar, use Desativar.`)) return;
    try { await api.delete(`/division-rules/${r.id}`); await load(); } catch (e) { setError(extractErrorMessage(e)); }
  };
  const reset = async () => {
    if (!window.confirm("Restaurar as regras padrão (Men's BB, Men's Physique, Classic, Wellness, Bikini, idade/experiência)? As regras atuais serão substituídas.")) return;
    try { const { data } = await api.post("/division-rules/reset-defaults"); setRules(data); } catch (e) { setError(extractErrorMessage(e)); }
  };

  const field = (k, label, type = "text", ph = "", w = "") => (
    <label className={`block ${w}`} key={k}>
      <span className="overline block mb-1">{label}</span>
      <input data-testid={`rule-${k}`} type={type} step="0.1" className="input-field" placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
    </label>
  );

  return (
    <div data-testid="division-rules">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="text-sm text-[var(--text-secondary)] max-w-2xl">
          Estas regras alimentam as <b>Categorias sugeridas</b> no cadastro de atleta. Uma regra casa com a categoria do campeonato pela
          <b> divisão</b> (palavra-chave no nome, ex.: MEN'S PHYSIQUE), pela <b>classe</b> (letra) e/ou por uma <b>palavra-chave extra</b> (ex.: 40+, JÚNIOR, NOVICE).
          Desativar mantém a regra salva, mas ela deixa de sugerir.
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost !py-2 !text-xs inline-flex items-center gap-1" onClick={reset} data-testid="rules-reset"><RotateCcw size={13} /> Restaurar padrões</button>
          <button className="btn-primary !py-2 !text-xs inline-flex items-center gap-1" onClick={() => { setError(""); setForm({ ...EMPTY }); }} data-testid="rules-new"><Plus size={13} /> Nova regra</button>
        </div>
      </div>
      {error && <div className="card p-3 mb-3 text-sm text-[#FF6B6B]" data-testid="rules-error">{error}</div>}

      {form && (
        <form onSubmit={save} className="card p-4 mb-4 space-y-3" data-testid="rule-form">
          <div className="flex items-center justify-between">
            <div className="overline">{form.id ? "Editar regra" : "Nova regra"}</div>
            <button type="button" className="btn-ghost !p-1" onClick={() => setForm(null)} data-testid="rule-cancel"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {field("nome", "Nome", "text", "Ex.: Men's Physique B", "col-span-2")}
            {field("divisao", "Divisão (no nome da categoria)", "text", "Ex.: MEN'S PHYSIQUE")}
            {field("classe", "Classe (letra)", "text", "A–G")}
            {field("palavra_chave", "Palavra-chave extra", "text", "Ex.: 40+, JÚNIOR, NOVICE")}
            <label className="block">
              <span className="overline block mb-1">Sexo</span>
              <select data-testid="rule-sexo" className="select-field w-full" value={form.sexo} onChange={(e) => setForm({ ...form, sexo: e.target.value })}>
                <option value="ambos">Ambos</option><option value="M">Masculino</option><option value="F">Feminino</option>
              </select>
            </label>
            <label className="block">
              <span className="overline block mb-1">Experiência</span>
              <select data-testid="rule-experiencia" className="select-field w-full" value={form.experiencia} onChange={(e) => setForm({ ...form, experiencia: e.target.value })}>
                <option value="">—</option><option value="novice">Novice (nunca venceu)</option><option value="true_novice">True Novice (1ª competição)</option>
              </select>
            </label>
            <label className="flex items-end gap-2 pb-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="rule-ativo" checked={!!form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} /> Ativa
            </label>
            {field("peso_min", "Peso mín. (kg, exclusivo)", "number", "> kg")}
            {field("peso_max", "Peso máx. (kg)", "number", "≤ kg")}
            {field("altura_min_cm", "Altura mín. (cm, exclusivo)", "number", "> cm")}
            {field("altura_max_cm", "Altura máx. (cm)", "number", "≤ cm")}
            {field("idade_min", "Idade mín.", "number", "Ex.: 35")}
            {field("idade_max", "Idade máx.", "number", "Ex.: 23")}
          </div>
          <div className="flex flex-wrap gap-1 items-center">
            <span className="text-[11px] text-[var(--text-secondary)] mr-1">Atalhos:</span>
            {PRESETS.map((p) => (
              <button key={p.label} type="button" className="btn-ghost !py-0.5 !px-2 !text-[10px]" onClick={() => setForm({ ...form, nome: form.nome || p.label, ...p.patch })} data-testid={`rule-preset-${p.label}`}>{p.label}</button>
            ))}
          </div>
          <div className="flex justify-end">
            <button className="btn-primary !py-2 !text-xs inline-flex items-center gap-1" disabled={busy} data-testid="rule-save"><Save size={13} /> {busy ? "Salvando..." : "Salvar regra"}</button>
          </div>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="table-dense" data-testid="rules-table">
          <thead>
            <tr><th>Regra</th><th>Divisão / Classe</th><th>Palavra-chave</th><th>Sexo</th><th>Peso</th><th>Altura</th><th>Idade / Exp.</th><th className="text-right pr-4">Ações</th></tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} data-testid={`rule-row-${r.id}`} className={r.ativo ? "" : "opacity-40"}>
                <td className="font-medium whitespace-nowrap">{r.nome}{!r.ativo && <span className="ml-2 text-[9px] tracking-widest text-[var(--text-secondary)]">INATIVA</span>}</td>
                <td className="text-xs">{r.divisao || "—"}{r.classe ? ` · ${r.classe}` : ""}</td>
                <td className="text-xs">{r.palavra_chave || "—"}</td>
                <td className="text-xs">{SEXO[r.sexo]}</td>
                <td className="text-xs tabular-nums">{rng(r.peso_min, r.peso_max, "kg")}</td>
                <td className="text-xs tabular-nums">{rng(r.altura_min_cm, r.altura_max_cm, "cm")}</td>
                <td className="text-xs">{r.idade_min != null ? `${r.idade_min}+ anos` : r.idade_max != null ? `até ${r.idade_max} anos` : "—"}{r.experiencia ? ` · ${r.experiencia === "novice" ? "Novice" : "True Novice"}` : ""}</td>
                <td className="text-right pr-4 whitespace-nowrap">
                  <button className="btn-ghost !p-1.5 mr-1" title={r.ativo ? "Desativar" : "Ativar"} onClick={() => toggle(r)} data-testid={`rule-toggle-${r.id}`}><Power size={13} /></button>
                  <button className="btn-ghost !p-1.5 mr-1" title="Editar" onClick={() => { setError(""); setForm(toForm(r)); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`rule-edit-${r.id}`}><Pencil size={13} /></button>
                  <button className="btn-ghost !p-1.5" title="Excluir" onClick={() => remove(r)} data-testid={`rule-delete-${r.id}`}><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
